import { Image } from 'canvas'
import { type ReactiveParam, reactiveParam } from '@/utils/reactive-param'
import { computed, unref } from 'vue'
import CanvasPainter from 'zrender/lib/canvas/Painter'
import SVGPainter from 'zrender/lib/svg/Painter'
import type { ECharts } from 'echarts'
import type { PainterBase } from 'zrender/lib/PainterBase'
import type { ZRenderType } from 'zrender'

/**
 * List of image formats to export.
 */
export const imageFormats = [
    {
        label: 'PNG',
        value: 'png'
    },
    {
        label: 'JPEG',
        value: 'jpeg'
    },
    {
        label: 'SVG',
        value: 'svg'
    }
] as const

/**
 * Width of the fake canvases.
 */
export const width = 1000

/**
 * Ratio used to calculcate height.
 */
export const ratio = 16 / 9

/**
 *
 */
export const height = width / ratio

/**
 * Export a given ECharts/ZRender instance into
 * an image.
 */
export function useImageExport<T extends ECharts | ZRenderType>(
    options: Partial<UseImageExportOptions<T>> = {}
) {
    /**
     * Full config options.
     */
    const config = {
        title: options.title ?? 'Image',
        formats: options.formats ?? imageFormats
    } as UseImageExportOptions<T>

    /**
     * Title of the image.
     */
    const title = reactiveParam(config.title)

    /**
     * List of menu items with action.
     */
    const menuItems = computed<MenuItem[]>(() => {
        return config.formats.map(format => ({
            label: `Export as ${format.label}`,
            action: () => exportImage(format.value)
        }))
    })

    /**
     * Zrender or ECharts instance.
     */
    const instanceRef = reactiveParam(options.instance)

    /**
     * Get the current painter.
     */
    function getPainter(instance: T, type: ImageFormats) {
        const painter =
            'painter' in instance ? instance.painter : instance.getZr().painter

        if (isSVGPainter(painter)) {
            if (type === 'svg') {
                return painter
            }

            const newPainter = new CanvasPainter(
                painter.root,
                painter.storage,
                {
                    height: painter.getHeight(),
                    width: painter.getWidth()
                },
                0
            )

            newPainter.refresh(true)

            return newPainter
        }

        if (isCanvasPainter(painter)) {
            if (type !== 'svg') {
                return painter
            }

            const newPainter = new SVGPainter(painter.root, painter.storage, {
                height: painter.getHeight(),
                width: painter.getWidth()
            })

            newPainter.refresh()

            return newPainter
        }
    }

    /**
     * Whether the given painter is Canvas.
     */
    function isCanvasPainter(painter?: PainterBase): painter is CanvasPainter {
        return painter?.type === 'canvas'
    }

    /**
     * Whether the given painter is SVG.
     */
    function isSVGPainter(painter?: PainterBase): painter is SVGPainter {
        return painter?.type === 'svg'
    }

    /**
     * Get the image data url.
     */
    function getDataUrl(instance: T, type: ImageFormats) {
        const painter = getPainter(instance, type)

        if (isCanvasPainter(painter)) {
            return painter
                .getRenderedCanvas({
                    backgroundColor: type === 'jpeg' ? 'white' : ''
                })
                .toDataURL(`image/${type}`, 1)
        } else if (isSVGPainter(painter)) {
            return painter.toDataURL(true)
        }
    }

    /**
     * Generate an image in the given format.
     */
    async function generateImage(type: ImageFormats) {
        const instance =
            (await options.getInstance?.(type)) || unref(instanceRef)

        if (!instance) return

        return getDataUrl(instance, type)
    }

    /**
     * Export an image.
     */
    async function exportImage(type: ImageFormats) {
        const dataUrl = await generateImage(type)

        if (!dataUrl) return

        const link = document.createElement('a')
        link.download = `${unref(title)}.${type}`
        link.href = dataUrl
        link.click()
    }

    return {
        menuItems,
        exportImage,
        generateImage
    }
}

export interface UseImageExportOptions<T extends ECharts | ZRenderType> {
    title: ReactiveParam<string>
    formats: { label: string; value: ImageFormats }[]
    instance: ReactiveParam<T | undefined>
    getInstance: (type: ImageFormats) => T | Promise<T>
    getDataUrl: (instance: T, type: ImageFormats) => string
    createImage: (src: string) => Promise<Image>
}

export type ImageFormats = (typeof imageFormats)[number]['value']
